import { Event } from 'store/api/event';
import { IdObject, Nullable } from 'types';
import { Carrier } from 'store/api/carriers';
import { Product } from 'store/api/product/types';
import { Supplier } from 'store/api/supplier/types';
import type { EventSplitRow } from 'components/stock-transfer-event-split/types';
import type {
  StockTransferEventAllocation,
  StockTransferEventAllocationRequest,
} from 'store/api/stock-transfers/types';

export enum StockOrderStatus {
  Pending = 'Pending',
  Shipped = 'Shipped',
  InTransit = 'InTransit',
  Exception = 'Exception',
  Shipment = 'Shipment',
  Done = 'Done',
}

export const StockOrderStatusLabel = {
  [StockOrderStatus.Done]: 'Done',
  [StockOrderStatus.Pending]: 'Pending',
  [StockOrderStatus.Shipped]: 'Shipped',
  [StockOrderStatus.Shipment]: 'Shipment',
  [StockOrderStatus.Exception]: 'Exception',
  [StockOrderStatus.InTransit]: 'In Transit',
};

export interface EditStockOrderFormValues
  extends
    Partial<
      Omit<
        StockOrder,
        | 'id'
        | 'date'
        | 'items'
        | 'carrier'
        | 'dateShipped'
        | 'estimatedDeliveryDate'
      >
    >,
    Pick<StockOrder, 'id'> {
  date?: Date;
  eventId?: number;
  carrierId?: number;
  supplierId?: number;
  dateShipped?: Date | null;
  eventIds?: number[] | null;
  // Owner 2026-10-16: the LTL price's event split rows (form only; sent as
  // `eventAllocations`). Not `events`: that is the order's linked events.
  eventShares?: EventSplitRow[];
  recipient?: 'event' | 'storage';
  orderTo?: 'storage' | 'terminal';
  // answers the "LTL Invoice # already on another record" check (409)
  confirmSharedLtlInvoice?: boolean;
  estimatedDeliveryDate?: Date | null;
  eventAllocations?:
    | StockOrderEventAllocation[]
    | StockTransferEventAllocationRequest[];
  items?: {
    count: string;
    price: number;
    productId: number;
    totalPrice: number;
  }[];
}

export interface StockOrder extends IdObject {
  date: string;
  amount: string;
  number: string;
  events: Event[];
  carrier?: Carrier;
  createdBy: number;
  ltlPrice?: string;
  storage?: Storage;
  workOrder?: string;
  supplier: Supplier;
  event?: Event | null;
  dateShipped?: string;
  phone?: string | null;
  trackingNumber: string;
  linkToWebsite?: string;
  address?: string | null;
  storageId: number | null;
  status: StockOrderStatus;
  items: StockOrderProduct[];
  contactName?: string | null;
  estimatedDeliveryDate: string;
  emergencyPhone?: string | null;
  // Russell Logistics invoice # for the LTL load (Invoice # = Load #).
  ltlInvoiceNumber?: string | null;
  // Owner 2026-10-16: the LTL price's event split (StockOrderEventAllocations,
  // same shape as a transfer's), each with its event's name.
  eventAllocations?: StockOrderEventAllocation[];
}

export type StockOrderEventAllocation = StockTransferEventAllocation;

export interface StockOrderProduct extends IdObject {
  count: number;
  cases: number;
  price: number;
  units: number;
  product: Product;
  productId: number;
  isShippingOnly?: Nullable<boolean>;
}

export interface StockOrderCompleteRequest extends IdObject {
  eventId?: number;
}

export type CreateStockOrderFormValues = Omit<EditStockOrderFormValues, 'id'>;

// L4a: PATCH inventory/stockorder/:id/ltl and inventory/transfer/:id/ltl set
// only these two columns. A body without either field writes nothing and
// answers the current values (used to learn whether Main has the route).
export interface StockLtlFields extends Partial<StockShippingFields> {
  id: number;
  status: string;
  ltlPrice: string | null;
  // owner 2026-10-16: the transfer route answers its work order
  workOrder?: string | null;
  ltlInvoiceNumber: string | null;
}

// T-F07 (owner 2026-10-15): the stock order route also sets and answers the
// shipment fields. '' = not set (NOT NULL columns). Owner 2026-10-19: the
// transfer route sets and answers carrierId + trackingNumber (null = not set).
export interface StockShippingFields {
  dateShipped: string;
  trackingNumber: string;
  carrierId: number | null;
  estimatedDeliveryDate: string;
}

export interface StockLtlEditRequest extends Partial<StockShippingFields> {
  id: number;
  // owner 2026-10-16: the LTL event split and (general storage) the order's
  // Events, saved with the LTL fields
  eventIds?: number[];
  ltlPrice?: string | null;
  // owner 2026-10-16: a transfer's work order (the transfer route only)
  workOrder?: string | null;
  ltlInvoiceNumber?: string | null;
  confirmSharedLtlInvoice?: boolean;
  eventAllocations?: StockTransferEventAllocationRequest[];
}
